import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { ReactiveFormsModule } from '@angular/forms';
import { filter, map, takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { MediaService } from '@letly/services';
import { UserProfileDropdownComponent } from '@letly/ui-common';
import { AsyncPipe, NgClass } from '@angular/common';
import { LandlordRoleService } from '../../core/services/landlord-role.service';
import { environment } from '../../../env/environment';
import { AuthService } from '@auth0/auth0-angular';
import { PosthogService } from '../../core/services/posthog.service';
import { SignOutService } from '../../core/services/sign-out.service';

interface NavItem {
  name: string;
  route: string;
}

const VERIFICATION_PATH = '/account/verification';

@Component({
  selector: 'app-navbar',
  imports: [
    RouterModule,
    MatIconModule,
    MatDividerModule,
    ReactiveFormsModule,
    UserProfileDropdownComponent,
    AsyncPipe,
    NgClass
  ],
  templateUrl: './navbar.component.html'
})
export class NavbarComponent implements OnInit, OnDestroy {

  protected readonly environment = environment;

  private readonly landlordRoleService = inject(LandlordRoleService);
  private readonly mediaService = inject(MediaService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly posthogService = inject(PosthogService);
  private readonly signOutService = inject(SignOutService);

  readonly isLandlord = this.landlordRoleService.isLandlord;
  readonly becomeLandlordLink = this.landlordRoleService.becomeLandlordLink;
  readonly hasProperties = this.landlordRoleService.hasProperties;
  readonly isAuthenticated$ = this.authService.isAuthenticated$;
  readonly posthogEnabled = this.posthogService.isEnabled;

  @Input() showNavbar = { desktop: true, mobile: false };
  @Input() showLinks = true;
  @Input() showAuthActions = true;

  private readonly destroy$ = new Subject<void>();

  readonly landlordNavItems: NavItem[] = [
    { name: $localize`:@@ui-common.navbar.landlord.listing:Listings`, route: '/landlord/listing' },
    { name: $localize`:@@ui-common.navbar.landlord.appointments:Appointments`, route: '/landlord/appointments' },
    { name: $localize`:@@ui-common.navbar.landlord.offers:Offers`, route: '/landlord/offers' },
    { name: $localize`:@@ui-common.navbar.landlord.tenancies:Tenancies`, route: '/landlord/tenancies' },
    { name: $localize`:@@ui-common.navbar.landlord.wallet:Wallet`, route: '/landlord/wallet' }
  ];

  readonly tenantNavItems: NavItem[] = [
    { name: $localize`:@@ui-common.navbar.tenant.home:Home`, route: '/list' },
    { name: $localize`:@@ui-common.navbar.tenant.viewings:Viewings`, route: '/viewings' },
    { name: $localize`:@@ui-common.navbar.tenant.offers:Offers`, route: '/offers' },
    { name: $localize`:@@ui-common.navbar.tenant.tenancies:Tenancies`, route: '/tenancies' }
  ];

  isMobile$: Observable<boolean>;
  currentUrl: string;

  ngOnInit(): void {
    this.isMobile$ = this.mediaService.mobileMediaChange;
    this.currentUrl = this.router.url;
    this.setupNavbarVisibility();

    const initial = this.getNavbarVisibilityFromRoute();
    this.showNavbar = initial.navbar ?? { desktop: true, mobile: false };
    this.showLinks = initial.links ?? true;
    this.showAuthActions = initial.authActions ?? true;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  effectiveIsLandlord(): boolean {
    if (this.currentUrl.startsWith(VERIFICATION_PATH)) {
      const tab = this.router.parseUrl(this.currentUrl).queryParams['tab'];
      if (tab === 'landlord' || tab === 'tenant') {
        return tab === 'landlord';
      }
    }
    return this.isLandlord();
  }

  signOut(): void {
    this.signOutService.signOut();
  }

  handleRoleSwitch(): void {
    if (this.currentUrl.startsWith(VERIFICATION_PATH)) {
      const newIsLandlord = !this.effectiveIsLandlord();
      this.landlordRoleService.setMode(newIsLandlord);
      this.router.navigate([VERIFICATION_PATH], { queryParams: { tab: newIsLandlord ? 'landlord' : 'tenant' } });
    } else {
      const route = this.effectiveIsLandlord() ? '/list' : this.becomeLandlordLink();
      this.router.navigateByUrl(route);
    }
  }

  isItemActive(item: NavItem): boolean {
    if (this.isLandlord()) {
      if (item.route === '/landlord/listing' && this.currentUrl.includes('/landlord/property-detail')) {
        return true;
      }
    }
    return this.router.isActive(item.route, {
      paths: 'subset',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored'
    });
  }

  private setupNavbarVisibility(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map((event) => {
        this.currentUrl = (event as NavigationEnd).urlAfterRedirects;
        return this.getNavbarVisibilityFromRoute();
      }),
      takeUntil(this.destroy$)
    ).subscribe(response => {
      this.showNavbar = response.navbar ?? { desktop: true, mobile: false };
      this.showLinks = response.links ?? true;
      this.showAuthActions = response.authActions ?? true;
    });
  }

  private getNavbarVisibilityFromRoute() {
    let snapshot = this.router.routerState.snapshot.root;
    while (snapshot.firstChild) {
      snapshot = snapshot.firstChild;
    }
    return {
      navbar: snapshot.data?.['showNavbar'],
      links: snapshot.data?.['showLinks'],
      authActions: snapshot.data?.['showAuthActions']
    };
  }
}
